import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase } from 'lucide-react';
import './CandidateGrid.scss';

interface Candidate {
  id: number;
  user_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
  description: string;
  profile_image_url?: string;
  work_experience: any[];
  education: any[];
  language_skills: any[];
  job_preferences: any;
  created_at: string;
  updated_at: string;
}

interface CandidateGridProps {
  candidates: Candidate[];
  layout: 'grid-3' | 'grid-2' | 'list';
  loading: boolean;
  isGuest?: boolean;
  selectedCandidates?: Set<number>;
  onToggleSelect?: (candidateId: number) => void;
}

const CandidateGrid: React.FC<CandidateGridProps> = ({ 
  candidates, 
  layout, 
  loading,
  isGuest = false,
  selectedCandidates = new Set(),
  onToggleSelect
}) => {
  const navigate = useNavigate();
  
  // Helper to get full profile image URL
  const getProfileImageUrl = (profileImageUrl?: string) => {
    if (!profileImageUrl) return null;
    if (profileImageUrl.startsWith('http')) return profileImageUrl;
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    // Remove /api if present since profile images are served from base URL
    const baseUrl = API_BASE_URL.replace('/api', '');
    return `${baseUrl}${profileImageUrl}`;
  };

  const handleCandidateClick = (candidateId: number, e: React.MouseEvent<HTMLDivElement>) => {
    if (isGuest) {
      navigate(`/register?type=employer&return=/candidate/${candidateId}`);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const fromRect = { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
    const currentUrl = window.location.pathname + window.location.search;
    const returnUrl = encodeURIComponent(currentUrl);
    navigate(`/candidate/${candidateId}?mode=employer&returnUrl=${returnUrl}`, { state: { fromRect } });
  };

  const toggleSelect = (candidateId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSelect?.(candidateId);
  };
  
  const getLatestPosition = (workExperience: any[]) => {
    if (!workExperience || workExperience.length === 0) return 'Ieškomas darbas';
    
    // Sort by end date to get the most recent position
    const sortedExperience = workExperience.sort((a, b) => {
      const dateA = new Date(a.endDate || '9999-12-31');
      const dateB = new Date(b.endDate || '9999-12-31');
      return dateB.getTime() - dateA.getTime();
    });
    
    return sortedExperience[0]?.position || 'Ieškomas darbas';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('lt-LT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getAge = (candidate: any) => {
    // Try to get birth date from different possible fields
    const birthDate = candidate.birth_date || candidate.birthDate || candidate.personalInfo?.birthDate;
    if (!birthDate) return 34; // Default age as fallback
    
    const today = new Date();
    const birth = new Date(birthDate);
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      return age - 1;
    }
    return age;
  };

  const getCity = (address: string) => {
    if (!address) return '';
    // Extract city from address (assuming format like "Street, City")
    const parts = address.split(',');
    return parts[parts.length - 1]?.trim() || address;
  };

  const getSalaryExpectation = (jobPreferences: any) => {
    if (!jobPreferences) return 'Nenurodyta';
    
    // Handle different salary formats
    if (jobPreferences.salaryMin && jobPreferences.salaryMax) {
      return `${jobPreferences.salaryMin} - ${jobPreferences.salaryMax} €`;
    }
    if (jobPreferences.salaryMin) {
      return `nuo ${jobPreferences.salaryMin} €`;
    }
    if (jobPreferences.salaryMax) {
      return `iki ${jobPreferences.salaryMax} €`;
    }
    if (jobPreferences.salary) {
      // Handle string format like "1500 EUR" or just number
      const salaryStr = String(jobPreferences.salary);
      if (salaryStr.includes('€') || salaryStr.includes('EUR')) {
        return salaryStr.replace('EUR', '€');
      }
      return `${salaryStr} €`;
    }
    
    return 'Nenurodyta';
  };

  const getExperienceDuration = (experience: any) => {
    if (!experience || !experience.startDate || !experience.endDate) {
      return '1metai ir 2 mėnesiai';
    }
    
    const start = new Date(experience.startDate);
    const end = new Date(experience.endDate);
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    let duration = '';
    if (years > 0) {
      duration += `${years}${years === 1 ? 'metai' : 'metai'}`;
    }
    if (remainingMonths > 0) {
      if (duration) duration += ' ir ';
      duration += `${remainingMonths} ${remainingMonths === 1 ? 'mėnesis' : 'mėnesiai'}`;
    }
    
    return duration || '1metai ir 2 mėnesiai';
  };

  if (loading) {
    return (
      <div className="candidate-grid loading">
        {[...Array(6)].map((_, index) => (
          <div key={index} className="candidate-card skeleton">
            <div className="candidate-avatar skeleton-avatar"></div>
            <div className="candidate-info">
              <div className="skeleton-line skeleton-name"></div>
              <div className="skeleton-line skeleton-position"></div>
              <div className="skeleton-line skeleton-details"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (candidates.length === 0) {
    return (
      <div className="no-candidates">
        <div className="no-candidates-icon">
          <Briefcase size={48} />
        </div>
        <h3>Kandidatų nerasta</h3>
        <p>Pabandykite pakeisti paieškos kriterijus arba išvalykite filtrus</p>
      </div>
    );
  }

  return (
    <div className={`candidate-grid ${layout}`}>
      {candidates.map((candidate) => {
        const isListView = layout === 'list';
        
        if (isListView) {
          // For guests in list view, show anonymized info
          const listDisplayName = isGuest 
            ? `Kandidatas ${candidate.first_name.charAt(0)}.` 
            : `${candidate.first_name} ${getAge(candidate)} m. ${getCity(candidate.address) || 'Vilnius'}`;
          const listAvatarName = isGuest ? 'K' : `${candidate.first_name}+${candidate.last_name}`;
          const listAvatarBg = isGuest ? '7a7a7a' : '7a7a7a';

          const listProfileImage = getProfileImageUrl(candidate.profile_image_url) || `https://ui-avatars.com/api/?name=${listAvatarName}&background=${listAvatarBg}&color=fff&size=200`;

          return (
            <div key={candidate.id} className={`candidate-card list-view ${isGuest ? 'guest-view' : ''}`} onClick={(e) => handleCandidateClick(candidate.id, e)}>
              <div className="card-photo-section">
                <div className="photo-wrapper">
                  <img 
                    src={listProfileImage}
                    alt={listDisplayName}
                    onError={(e) => {
                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${listAvatarName}&background=${listAvatarBg}&color=fff&size=200`;
                    }}
                  />
                  <div className="name-overlay">
                    {listDisplayName}
                  </div>
                </div>
              </div>
              
              <div className="card-content-section">
                <div className="content-row">
                  <span className="label">Ieškomas darbas:</span>
                  <span className="value">{getLatestPosition(candidate.work_experience)}</span>
                </div>
                
                {!isGuest && (
                  <div className="content-row">
                    <span className="label">Darbo patirtis (paskutinė pozicija):</span>
                    <span className="value">
                      {candidate.work_experience && candidate.work_experience.length > 0 
                        ? `${candidate.work_experience[0]?.companyName || 'UAB Green'}, ${candidate.work_experience[0]?.position || 'pardavimų vadybininkas'} (${getExperienceDuration(candidate.work_experience[0])})`
                        : 'UAB Green, pardavimų vadybininkas (1metai ir 2 mėnesiai)'
                      }
                    </span>
                  </div>
                )}
                
                <div className="content-row">
                  <span className="label">Pageidaujamas atlyginimas:</span>
                  <span className={`value salary-value ${isGuest ? 'hidden-salary' : ''}`}>
                    {isGuest ? '***' : getSalaryExpectation(candidate.job_preferences)}
                  </span>
                </div>

                {isGuest && (
                  <div className="content-row guest-cta">
                    <span className="value guest-hint">Registruokitės, kad pamatytumėte daugiau informacijos</span>
                  </div>
                )}
              </div>
              
              <div className="card-date-section">
                <div className="date-info">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12,6 12,12 16,14"/>
                  </svg>
                  <span className="date-label">Paskutinis aktyvumas</span>
                </div>
                <div className="date-value">{formatDate(candidate.updated_at)}</div>
              </div>
            </div>
          );
        }
        
        // Grid view (original layout)
        // For guests, show anonymized/limited info
        const displayName = isGuest 
          ? `Kandidatas ${candidate.first_name.charAt(0)}.` 
          : `${candidate.first_name} ${candidate.last_name}`;
        const displayBasicInfo = isGuest
          ? 'Registruokitės, kad pamatytumėte daugiau'
          : `${candidate.first_name} ${getAge(candidate)} m. ${getCity(candidate.address) || 'Vilnius'}`;
        const displaySalary = isGuest 
          ? '***' 
          : getSalaryExpectation(candidate.job_preferences);
        const avatarBg = isGuest ? '7a7a7a' : '0D8ABC';
        const avatarName = isGuest ? 'K' : `${candidate.first_name}+${candidate.last_name}`;
        const profileImage = getProfileImageUrl(candidate.profile_image_url) || `https://ui-avatars.com/api/?name=${avatarName}&background=${avatarBg}&color=fff&size=80`;

        return (
          <div key={candidate.id} className={`candidate-card ${isGuest ? 'guest-view' : ''}`} onClick={(e) => handleCandidateClick(candidate.id, e)}>
            <div className="card-header">
              {!isGuest && (
                <button 
                  className={`bookmark-button ${selectedCandidates.has(candidate.id) ? 'bookmarked' : ''}`}
                  onClick={(e) => toggleSelect(candidate.id, e)}
                  title={selectedCandidates.has(candidate.id) ? 'Panaikinti žymėjimą' : 'Pažymėti'}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill={selectedCandidates.has(candidate.id) ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                  </svg>
                </button>
              )}
            </div>
            
            <div className="candidate-avatar">
              <img 
                src={profileImage}
                alt={displayName}
                onError={(e) => {
                  e.currentTarget.src = `https://ui-avatars.com/api/?name=${avatarName}&background=${avatarBg}&color=fff&size=80`;
                }}
              />
            </div>
            
            <div className="candidate-info">
              <h3 className="candidate-name">
                {displayName}
              </h3>
              <div className="candidate-basic-info">
                <span className={`age-city ${isGuest ? 'guest-hint' : ''}`}>
                  {displayBasicInfo}
                </span>
              </div>
              
              <div className="candidate-details">
                <div className="detail-section">
                  <h4 className="section-title">Ieškomas darbas</h4>
                  <p className="section-content">{getLatestPosition(candidate.work_experience)}</p>
                </div>
                
                {!isGuest && (
                  <div className="detail-section">
                    <h4 className="section-title">Darbo patirtis (paskutinė pozicija)</h4>
                    <p className="section-content">
                      {candidate.work_experience && candidate.work_experience.length > 0 
                        ? `${candidate.work_experience[0]?.companyName || 'UAB Green'}, ${candidate.work_experience[0]?.position || 'pardavimų vadybininkas'} (${getExperienceDuration(candidate.work_experience[0])})`
                        : 'UAB Green, pardavimų vadybininkas (1metai ir 2 mėnesiai)'
                      }
                    </p>
                  </div>
                )}
                
                <div className="detail-section salary">
                  <h4 className="section-title">Pageidaujamas atlyginimas:</h4>
                  <p className={`section-content salary-amount ${isGuest ? 'hidden-salary' : ''}`}>{displaySalary}</p>
                </div>
              </div>
            </div>
            
            <div className="card-footer">
              <div className="footer-info">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
                <span>Paskutinis aktyvumas</span>
              </div>
              <div className="footer-date">
                {formatDate(candidate.updated_at)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CandidateGrid;
